/**
 * Filter state (brief §7).
 *
 * Two copies are kept apart on purpose: `draft` is what the panel edits, and
 * `applied` is what the map and stats actually use. The "Применить" button
 * copies draft → applied; until then the button shows how many parameters
 * changed. Removing an active-filter chip clears that one field in both and
 * re-applies immediately, since a chip represents something already in effect.
 *
 * Period is stored as concrete ISO dates; empty string means "open" (no bound),
 * which reads as the full dataset range. This sidesteps having to wait for the
 * dataset bounds before the store can exist.
 */
import { create } from "zustand";

import type { Satellite } from "../data/types";

export type CountMode = "extent" | "country";
export type DatePreset = "7d" | "30d" | "90d" | "1y" | "all";

export interface FilterValues {
  /** ISO date (YYYY-MM-DD) or "" for open. */
  from: string;
  to: string;
  /** Empty = all satellites. */
  satellites: Satellite[];
  /** kg/h; 0 = no minimum. */
  minRate: number;
}

const EMPTY: FilterValues = { from: "", to: "", satellites: [], minRate: 0 };

function sameValues(a: FilterValues, b: FilterValues): boolean {
  return (
    a.from === b.from &&
    a.to === b.to &&
    a.minRate === b.minRate &&
    a.satellites.length === b.satellites.length &&
    a.satellites.every((s) => b.satellites.includes(s))
  );
}

/** Number of parameters that differ between draft and applied (for the button). */
export function countChanges(draft: FilterValues, applied: FilterValues): number {
  let n = 0;
  if (draft.from !== applied.from || draft.to !== applied.to) n += 1;
  if (
    draft.satellites.length !== applied.satellites.length ||
    !draft.satellites.every((s) => applied.satellites.includes(s))
  ) {
    n += 1;
  }
  if (draft.minRate !== applied.minRate) n += 1;
  return n;
}

const DAY = 86_400_000;

interface FiltersState {
  /** Dataset bounds, set once meta loads. */
  range: { min: string; max: string };
  draft: FilterValues;
  applied: FilterValues;
  countMode: CountMode;

  setRange: (min: string, max: string) => void;
  setDraftPeriod: (from: string, to: string) => void;
  applyPreset: (preset: DatePreset) => void;
  toggleSatellite: (satellite: Satellite) => void;
  /** Set the whole draft satellite list (used by grouped controls). */
  setDraftSatellites: (satellites: Satellite[]) => void;
  setMinRate: (value: number) => void;
  apply: () => void;
  reset: () => void;
  /** Clear one applied field (chip ✕) and re-apply. */
  clearApplied: (field: "period" | "satellites" | "minRate") => void;
  /** Remove satellites from the applied set (satellite chip ✕) and re-apply. */
  removeAppliedSatellites: (satellites: Satellite[]) => void;
  setCountMode: (mode: CountMode) => void;
  /** Replace applied+draft from a URL snapshot. */
  hydrate: (values: Partial<FilterValues>, countMode?: CountMode) => void;
}

export const useFiltersStore = create<FiltersState>((set, get) => ({
  range: { min: "", max: "" },
  draft: { ...EMPTY },
  applied: { ...EMPTY },
  countMode: "country",

  setRange: (min, max) => set({ range: { min, max } }),

  setDraftPeriod: (from, to) => set({ draft: { ...get().draft, from, to } }),

  applyPreset: (preset) => {
    const { range, draft } = get();
    if (preset === "all") {
      set({ draft: { ...draft, from: "", to: "" } });
      return;
    }
    const max = range.max ? Date.parse(range.max) : Date.now();
    const spanDays = preset === "7d" ? 7 : preset === "30d" ? 30 : preset === "90d" ? 90 : 365;
    const from = new Date(max - spanDays * DAY).toISOString().slice(0, 10);
    const to = new Date(max).toISOString().slice(0, 10);
    // Don't run past the dataset start.
    const clampedFrom = range.min && from < range.min ? range.min : from;
    set({ draft: { ...draft, from: clampedFrom, to } });
  },

  toggleSatellite: (satellite) => {
    const list = get().draft.satellites;
    set({
      draft: {
        ...get().draft,
        satellites: list.includes(satellite)
          ? list.filter((s) => s !== satellite)
          : [...list, satellite],
      },
    });
  },

  setDraftSatellites: (satellites) => set({ draft: { ...get().draft, satellites } }),

  setMinRate: (value) => set({ draft: { ...get().draft, minRate: Math.max(0, value) } }),

  apply: () => set({ applied: { ...get().draft } }),

  reset: () => set({ draft: { ...EMPTY }, applied: { ...EMPTY } }),

  clearApplied: (field) => {
    const applied = { ...get().applied };
    if (field === "period") {
      applied.from = "";
      applied.to = "";
    } else if (field === "satellites") {
      applied.satellites = [];
    } else {
      applied.minRate = 0;
    }
    set({ applied, draft: { ...applied } });
  },

  removeAppliedSatellites: (satellites) => {
    const applied = {
      ...get().applied,
      satellites: get().applied.satellites.filter((s) => !satellites.includes(s)),
    };
    set({ applied, draft: { ...applied } });
  },

  setCountMode: (mode) => set({ countMode: mode }),

  hydrate: (values, countMode) => {
    const next: FilterValues = { ...EMPTY, ...values };
    set({
      applied: next,
      draft: { ...next },
      ...(countMode ? { countMode } : {}),
    });
  },
}));

export { sameValues };
